import type { PropsWithChildren, RefObject } from 'react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

export type PartialRenderProps<Item> = PropsWithChildren<{
  items: Item[];
  disabled?: boolean;
  elementRef: RefObject<HTMLElement>;
  method: 'scroll' | 'delay' | 'touch-window';
  partSize: number;
  delay?: number;
  onItemRender: (item: Item, index: number) => React.ReactNode;
}>;

const PartialRender = <I,>({
  items,
  disabled,
  elementRef,
  onItemRender,
  partSize,
  method,
  delay = 10,
}: PartialRenderProps<I>) => {
  const [limitDisplayProducts, setLimitDisplayProducts] = useState({
    enabled: !disabled,
    limit: partSize,
  });

  const { ref } = useInView({
    delay,
    skip: method !== 'scroll' || !limitDisplayProducts.enabled,
    rootMargin: '100px 0px 0px 0px',
    onChange: (inView) => {
      if (inView) {
        setLimitDisplayProducts((prev) => {
          const limit = prev.limit + partSize;
          if (limit >= items.length) {
            return {
              limit: items.length,
              enabled: false,
            };
          }

          return {
            ...prev,
            limit,
            enabled: true,
          };
        });
      }
    },
  });

  useEffect(() => {
    if (method === 'delay' && limitDisplayProducts.enabled) {
      const interval = setInterval(() => {
        setLimitDisplayProducts((prev) => {
          const limit = prev.limit + partSize;
          if (limit >= items.length) {
            clearInterval(interval);

            return {
              limit: items.length,
              enabled: false,
            };
          }

          return {
            ...prev,
            limit,
            enabled: true,
          };
        });
      }, delay);

      return () => {
        clearInterval(interval);
      };
    }
  }, [method, limitDisplayProducts.enabled]);

  useEffect(() => {
    const elm = elementRef.current;
    if (method === 'touch-window' && limitDisplayProducts.enabled && elm) {
      const onTouch = () => {
        setLimitDisplayProducts((prev) => ({
          ...prev,
          enabled: false,
        }));
      };

      /**
       * Use 'touchmove' to avoid interfering with click events for example: click on theme search input to open ISW
       */
      window.addEventListener('touchmove', onTouch);

      return () => {
        window.addEventListener('touchmove', onTouch);
      };
    }
  }, [method, limitDisplayProducts.enabled]);

  useEffect(() => {
    if (elementRef.current?.lastElementChild) {
      ref(elementRef.current.lastElementChild);
    }
  }, [elementRef.current?.lastElementChild]);

  const displayItems = useMemo(() => {
    if (limitDisplayProducts.enabled) return items.slice(0, limitDisplayProducts.limit);

    return items;
  }, [items, limitDisplayProducts]);

  return <>{displayItems.map(onItemRender)}</>;
};

export default PartialRender;
